import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { autoPostAPI, propertiesAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';
import {
  X, Eye, Loader2, Instagram, Facebook, Youtube, Twitter, Linkedin,
  RefreshCw, Copy, Check, Sparkles, Info, Home, ArrowRight,
} from 'lucide-react';

// TikTok icon. Lucide doesn't ship one.
const TikTok = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const PLATFORM_META = [
  { key: 'instagram', label: 'Instagram', icon: Instagram, accent: 'text-pink-400' },
  { key: 'facebook',  label: 'Facebook',  icon: Facebook,  accent: 'text-blue-400' },
  { key: 'tiktok',    label: 'TikTok',    icon: TikTok,    accent: 'text-cyan-400' },
  { key: 'youtube',   label: 'YouTube Shorts', icon: Youtube, accent: 'text-[#b3543f]' },
  { key: 'x',         label: 'X',         icon: Twitter,   accent: 'text-[#4a463b]' },
  { key: 'linkedin',  label: 'LinkedIn',  icon: Linkedin,  accent: 'text-sky-400' },
];

/**
 * Caption preview modal. Fires /auto-post/preview-captions and shows
 * the per-platform captions that would be generated for a given
 * property under the user's current (or in-progress) caption_style.
 *
 * The "Live preview" flow lets users tune caption_style settings
 * iteratively without firing a render. Each click costs ~$0.01 in
 * OpenAI tokens. Acceptable for an explicit preview action.
 *
 * Props
 *   onClose()
 *   captionStyleOverride. Pass the in-progress style so the user
 *     can preview UNSAVED changes. Falls back to saved config on the
 *     server if null.
 *   propertyOptions. Array of {id, title} to seed the property
 *     picker. Loaded by parent (AutoPostSettingsPage already has
 *     properties for the queue tab).
 */
export default function CaptionPreview({ onClose, captionStyleOverride = null, propertyOptions = [] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [props, setProps] = useState(propertyOptions);
  // Track whether the initial load has finished so we can distinguish
  // "still loading" (don't show empty state) from "actually 0 props"
  // (show the friendly redirect). Without this the empty-state flashes
  // for a moment while propertiesAPI.getAll is in flight.
  const [propsLoaded, setPropsLoaded] = useState(propertyOptions.length > 0);
  const [selectedPropertyId, setSelectedPropertyId] = useState(
    propertyOptions[0]?.id || null
  );
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);  // {social_payload, per_platform_captions, property}
  const [copiedKey, setCopiedKey] = useState(null);

  // Lazy-load properties if the parent didn't supply any.
  useEffect(() => {
    let cancelled = false;
    if (props.length === 0) {
      propertiesAPI.getAll().then((r) => {
        if (cancelled) return;
        const rows = Array.isArray(r.data) ? r.data : [];
        const slim = rows.map((p) => ({ id: p.id, title: p.title }));
        setProps(slim);
        if (!selectedPropertyId && slim[0]) setSelectedPropertyId(slim[0].id);
        setPropsLoaded(true);
      }).catch(() => { if (!cancelled) setPropsLoaded(true); });
    } else {
      setPropsLoaded(true);
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const runPreview = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await autoPostAPI.previewCaptions(
        selectedPropertyId,
        captionStyleOverride,
      );
      setResult(res.data || null);
    } catch (e) {
      toast.error(t('autoPost.preview.failed') ||
        'Vorschau konnte nicht generiert werden. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  const copyCaption = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (_) {
      toast.error(t('common.copyFailed') || 'Konnte nicht kopieren.');
    }
  };

  const captions = result?.per_platform_captions || {};

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="glass border-2 border-[#1f4d3a]/30 w-full sm:max-w-2xl max-h-[92dvh] sm:max-h-[90dvh] overflow-y-auto overscroll-contain rounded-t-2xl sm:rounded-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-[#fffdf7]/95 backdrop-blur-md border-b border-[#e3dccb] px-5 sm:px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-semibold text-[#23211a] flex items-center gap-2">
            <Eye className="w-5 h-5 text-[#1f4d3a]" />
            {t('autoPost.preview.title') || 'Caption-Vorschau pro Plattform'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-[#efe9db] text-[#6f6a5c] hover:text-[#23211a] transition"
            aria-label={t('common.close') || 'Close'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 sm:px-6 py-5 space-y-4">
          {/* Audit fix: explicit empty state when the user has zero
              properties. Previously the modal showed a disabled
              dropdown + "pick a property" hint that pointed nowhere.
              Now: explain WHY there's nothing to preview + a button
              that takes them to import a property. */}
          {propsLoaded && props.length === 0 ? (
            <div className="text-center py-8 space-y-4">
              <div className="inline-flex p-4 bg-[#efe9db] rounded-2xl">
                <Home className="w-8 h-8 text-[#6f6a5c]" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-[#23211a] mb-1">
                  {t('autoPost.preview.emptyTitle') || 'Erst eine Immobilie hinzufügen'}
                </h3>
                <p className="text-xs text-[#6f6a5c] max-w-sm mx-auto">
                  {t('autoPost.preview.emptyHint') ||
                    'Caption-Vorschau braucht eine Immobilie als Vorlage. Verbinde Justimmo, importiere von Willhaben oder leg eine manuell an.'}
                </p>
              </div>
              <Button
                type="button"
                onClick={() => { onClose?.(); navigate('/properties'); }}
                className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white"
              >
                {t('autoPost.preview.emptyCta') || 'Zu meinen Immobilien'}
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          ) : (
          <>
          {/* Why card */}
          <div className="p-3 rounded-lg bg-[#f0f3ec] border border-[#1f4d3a]/20 flex items-start gap-2">
            <Info className="w-4 h-4 text-[#1f4d3a] mt-0.5 shrink-0" />
            <p className="text-xs text-[#23211a]/90 leading-relaxed">
              {t('autoPost.preview.why') ||
                'So sehen deine Captions auf jeder Plattform aus. Mit deinen aktuellen Stil-Einstellungen. Probier verschiedene Immobilien, optimiere die Einstellungen, sieh sofort das Ergebnis.'}
            </p>
          </div>

          {/* Property picker + Generate button */}
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={selectedPropertyId || ''}
              onChange={(e) => setSelectedPropertyId(e.target.value || null)}
              className="bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-sm text-[#23211a] focus:outline-none focus:ring-2 focus:ring-[#1f4d3a]/40 flex-1"
              data-testid="caption-preview-property-select"
            >
              {props.map((p) => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
            <Button
              type="button"
              onClick={runPreview}
              disabled={loading || !selectedPropertyId}
              className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white shrink-0"
              data-testid="caption-preview-generate-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('autoPost.preview.generating') || 'Wird generiert…'}</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />
                  {result ? (t('autoPost.preview.regenerate') || 'Neu generieren')
                          : (t('autoPost.preview.generate') || 'Vorschau erstellen')}</>
              )}
            </Button>
          </div>

          {/* Results */}
          {!result && !loading && (
            <p className="text-sm text-[#8a8474] text-center py-8">
              {t('autoPost.preview.placeholder') ||
                'Wähle eine Immobilie und klick „Vorschau erstellen", um die Captions zu sehen.'}
            </p>
          )}

          {loading && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin mx-auto" />
              <p className="text-xs text-[#6f6a5c] mt-2">
                {t('autoPost.preview.generating') || 'Wird generiert…'}
              </p>
            </div>
          )}

          {result && Object.keys(captions).length > 0 && (
            <div className="space-y-3">
              {PLATFORM_META.filter((p) => captions[p.key]).map(({ key, label, icon: Icon, accent }) => {
                const text = captions[key];
                const isCopied = copiedKey === key;
                return (
                  <div key={key} className="rounded-lg border border-[#e3dccb] bg-[#f7f3ea]/40 overflow-hidden">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-white border-b border-[#e3dccb]">
                      <span className={`text-xs font-medium flex items-center gap-1.5 ${accent}`}>
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                        <span className="text-[10px] text-[#8a8474] ml-1">({text.length} Z.)</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => copyCaption(key, text)}
                        className="text-[11px] text-[#6f6a5c] hover:text-[#23211a] inline-flex items-center gap-1"
                      >
                        {isCopied
                          ? <><Check className="w-3 h-3 text-[#1e7a4f]" /> {t('common.copied') || 'Kopiert'}</>
                          : <><Copy className="w-3 h-3" /> {t('common.copy') || 'Kopieren'}</>
                        }
                      </button>
                    </div>
                    <pre className="px-3 py-2.5 text-xs text-[#23211a] whitespace-pre-wrap break-words font-sans leading-relaxed">
                      {text}
                    </pre>
                  </div>
                );
              })}
            </div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}
